import { toPagingOnlyVariables, toScalarListVariables } from '@/graphql/list-query';
import { graphqlClient } from '@/graphql/graphql-client';
import {
  GET_TASK_COMMENTS_QUERY,
  GET_TASK_MANAGER_QUERY,
  GET_TASK_MANAGER_SECTIONS_QUERY,
  GET_TASK_MANAGER_TAGS_QUERY,
} from '@/graphql/queries';
import type {
  TaskItem,
  ItemTag,
  GetCommentsResponse,
  TaskCommentUpdateInput,
  TaskCommentInsertInput,
  TaskItemInsertInput,
  TaskItemUpdateInput,
  TaskSectionInsertInput,
  TaskSectionUpdateInput,
  PaginationParams,
  GetTasksResponse,
  GetSectionsResponse,
  UpdateTaskManagerSectionResponse,
  GetTagsResponse,
  TaskTagInsertInput,
  TaskTagUpdateInput,
} from '../types/task-manager.types';
import {
  INSERT_TASK_MANAGER_ITEM_MUTATION,
  UPDATE_TASK_MANAGER_ITEM_MUTATION,
  DELETE_TASK_MANAGER_ITEM_MUTATION,
  INSERT_TASK_MANAGER_SECTION_MUTATION,
  UPDATE_TASK_MANAGER_SECTION_MUTATION,
  DELETE_TASK_MANAGER_SECTION_MUTATION,
  INSERT_TASK_MANAGER_TAG_MUTATION,
  UPDATE_TASK_MANAGER_TAG_MUTATION,
  DELETE_TASK_MANAGER_TAG_MUTATION,
  INSERT_TASK_COMMENTS_MUTATION,
  UPDATE_TASK_COMMENTS_MUTATION,
  DELETE_TASK_COMMENTS_MUTATION,
} from '@/graphql/mutations';
import { clients } from '@/lib/https';
import { GetUsersPayload, IamData } from '../types/user.types';

import type {
  InsertTaskItemResponse,
  UpdateTaskItemResponse,
  DeleteTaskItemResponse,
  InsertTaskSectionResponse,
  DeleteTaskSectionResponse,
  InsertTaskTagResponse,
  UpdateTaskTagResponse,
  DeleteTaskTagResponse,
  InsertTaskCommentResponse,
  UpdateTaskCommentResponse,
  DeleteTaskCommentResponse,
} from '../types/task-manager.types';

export type {
  BaseMutationResponse,
  InsertTaskItemResponse,
  UpdateTaskItemResponse,
  DeleteTaskItemResponse,
  InsertTaskSectionResponse,
  UpdateTaskSectionResponse,
  DeleteTaskSectionResponse,
  InsertTaskTagResponse,
  UpdateTaskTagResponse,
  DeleteTaskTagResponse,
  InsertTaskCommentResponse,
  UpdateTaskCommentResponse,
  DeleteTaskCommentResponse,
  InsertTaskAttachmentResponse,
  UpdateTaskAttachmentResponse,
  DeleteTaskAttachmentResponse,
} from '../types/task-manager.types';

/**
 * Task Manager Service
 *
 * This service provides GraphQL-based operations for task management.
 * It handles all CRUD operations for tasks and sections.
 */

/**
 * Fetches paginated task items with filtering and sorting
 * @param params - Query parameters including pagination and filters
 * @returns Promise with task items data
 */

export const getTasks = async (params: PaginationParams): Promise<GetTasksResponse> => {
  const variables = toPagingOnlyVariables(params);
  const { pageNo, pageSize } = variables;

  try {
    const response = await graphqlClient.query({
      query: GET_TASK_MANAGER_QUERY,
      variables,
    });

    const taskManagerItems = response.getTaskManagerItems;

    if (taskManagerItems) {
      const items = (taskManagerItems.items || []).map((task) => {
        const isItemTag = (tag: unknown): tag is ItemTag =>
          typeof tag === 'object' &&
          tag !== null &&
          'ItemId' in tag &&
          typeof tag.ItemId === 'string' &&
          'TagLabel' in tag &&
          typeof tag.TagLabel === 'string';

        const itemTags: ItemTag[] = Array.isArray(task.ItemTag)
          ? task.ItemTag.filter(isItemTag)
          : [];

        const legacyTags = Array.isArray(task.Tags) ? task.Tags : [];

        const convertedTags = itemTags.length > 0 ? itemTags : legacyTags.map(normalizeToItemTag);

        return {
          ...task,
          Assignee: Array.isArray(task.Assignee) ? task.Assignee : [],
          ItemTag: convertedTags,
          ItemTags: convertedTags.map((tag) => tag.TagLabel || tag.ItemId),
          OrganizationIds: Array.isArray(task.OrganizationIds) ? task.OrganizationIds : [],
        } satisfies TaskItem;
      });

      return {
        TaskManagerItems: {
          items,
          totalCount: taskManagerItems.totalCount || 0,
          hasNextPage: taskManagerItems.hasNextPage || false,
          hasPreviousPage: taskManagerItems.hasPreviousPage || false,
          pageSize: taskManagerItems.pageSize || pageSize,
          pageNo: taskManagerItems.pageNo || pageNo,
          totalPages: taskManagerItems.totalPages || 1,
        },
      };
    }
    console.warn('Unexpected response structure, returning default sections');
    return {
      TaskManagerItems: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  } catch (error) {
    console.error('Error fetching task items:', error);
    return {
      TaskManagerItems: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  }
};

/**
 * Fetches task sections with pagination
 * @param params - Pagination parameters
 * @returns Promise with task sections data
 */
export const getTaskSections = async (params: PaginationParams): Promise<GetSectionsResponse> => {
  const variables = toPagingOnlyVariables(params);
  const { pageNo, pageSize } = variables;

  try {
    const response = await graphqlClient.query({
      query: GET_TASK_MANAGER_SECTIONS_QUERY,
      variables,
    });

    const taskManagerSections = response.getTaskManagerSections;

    if (taskManagerSections) {
      return {
        TaskManagerSections: {
          items: taskManagerSections.items || [],
          totalCount: taskManagerSections.totalCount || 0,
          hasNextPage: taskManagerSections.hasNextPage || false,
          hasPreviousPage: taskManagerSections.hasPreviousPage || false,
          pageSize: taskManagerSections.pageSize || pageSize,
          pageNo: taskManagerSections.pageNo || pageNo,
          totalPages: taskManagerSections.totalPages || 1,
        },
      };
    }
    console.warn('Unexpected response structure, returning default sections');
    return {
      TaskManagerSections: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  } catch (error) {
    console.error('Error fetching task sections:', error);
    return {
      TaskManagerSections: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  }
};

/**
 * Fetches task sections with pagination
 * @param params - Pagination parameters
 * @returns Promise with task sections data
 */
export const getTaskTags = async (params: PaginationParams): Promise<GetTagsResponse> => {
  const variables = toPagingOnlyVariables(params);
  const { pageNo, pageSize } = variables;

  try {
    const response = await graphqlClient.query({
      query: GET_TASK_MANAGER_TAGS_QUERY,
      variables,
    });

    const taskManagerTags = response.getTaskManagerTags;

    if (taskManagerTags) {
      return {
        TaskManagerTags: {
          items: taskManagerTags.items || [],
          totalCount: taskManagerTags.totalCount || 0,
          hasNextPage: taskManagerTags.hasNextPage || false,
          hasPreviousPage: taskManagerTags.hasPreviousPage || false,
          pageSize: taskManagerTags.pageSize || pageSize,
          pageNo: taskManagerTags.pageNo || pageNo,
          totalPages: taskManagerTags.totalPages || 1,
        },
      };
    }
    console.warn('Unexpected response structure, returning default sections');
    return {
      TaskManagerTags: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  } catch (error) {
    console.error('Error fetching task sections:', error);
    return {
      TaskManagerTags: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  }
};

/**
 * Fetches task comments with pagination
 * @param params - Pagination parameters
 * @returns Promise with task comments data
 */
export const getTaskComments = async (params: PaginationParams): Promise<GetCommentsResponse> => {
  const variables = toScalarListVariables(params, { taskId: 'TaskId' });
  const { pageNo, pageSize } = variables;

  try {
    const response = await graphqlClient.query({
      query: GET_TASK_COMMENTS_QUERY,
      variables,
    });

    const taskComments = response.getTaskComments;

    if (taskComments) {
      const processedComments = (taskComments.items || []).map((comment) => ({
        ...comment,
        ItemId: comment.ItemId || '',
        Content: comment.Content || '',
        Timestamp: comment.Timestamp || new Date().toISOString(),
        Author: comment.Author || '',
        CreatedBy: comment.CreatedBy || '',
        CreatedDate: comment.CreatedDate || new Date().toISOString(),
        LastUpdatedBy: comment.LastUpdatedBy || comment.CreatedBy || '',
        LastUpdatedDate: comment.LastUpdatedDate || comment.CreatedDate || new Date().toISOString(),
        TaskId: comment.TaskId || '',
      }));

      return {
        TaskManagerComments: {
          items: processedComments,
          totalCount: taskComments.totalCount || processedComments.length,
          hasNextPage: taskComments.hasNextPage || false,
          hasPreviousPage: taskComments.hasPreviousPage || false,
          pageSize: taskComments.pageSize || pageSize,
          pageNo: taskComments.pageNo || pageNo,
          totalPages: taskComments.totalPages || 1,
        },
      };
    }

    console.warn('Unexpected response structure, returning empty comments');
    return {
      TaskManagerComments: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  } catch (error) {
    console.error('Error fetching task sections:', error);
    return {
      TaskManagerComments: {
        items: [],
        totalCount: 0,
        hasNextPage: false,
        hasPreviousPage: false,
        pageSize,
        pageNo,
        totalPages: 0,
      },
    };
  }
};

/**
 * Creates a new task item
 * @param input - Task item data
 * @returns Promise with creation result
 */

// Helper function to normalize tags to ItemTag format
const normalizeToItemTag = (tag: string | ItemTag): ItemTag => {
  if (typeof tag === 'string') {
    return { ItemId: tag, TagLabel: tag };
  }
  return {
    ItemId: tag.ItemId || '',
    TagLabel: tag.TagLabel || tag.ItemId || '',
  };
};

// Helper function to get normalized tags from input
const getNormalizedTags = (input: {
  ItemTag?: ItemTag[];
  Tags?: Array<string | ItemTag>;
}): ItemTag[] => {
  if (input.ItemTag) {
    return input.ItemTag.map(normalizeToItemTag);
  }
  if (input.Tags) {
    return input.Tags.map(normalizeToItemTag);
  }
  return [];
};

export const createTaskItem = async (
  input: TaskItemInsertInput
): Promise<InsertTaskItemResponse> => {
  try {
    const formattedInput = {
      ...input,
      ItemTag: getNormalizedTags(input),
      Tags: undefined,
    };

    const response = await graphqlClient.request(INSERT_TASK_MANAGER_ITEM_MUTATION, formattedInput);

    const responseData = response;

    if (!responseData) {
      throw new Error('No response data received from server');
    }

    if (!responseData.insertTaskManagerItem?.itemId) {
      throw new Error('No task ID in response');
    }

    return {
      insertTaskManagerItem: {
        ...responseData.insertTaskManagerItem,
        itemId: responseData.insertTaskManagerItem.itemId,
      },
    };
  } catch (error) {
    console.error('Error in createTaskItem:', error);
    throw error;
  }
};

/**
 * Updates an existing task item
 * @param itemId - ID of the task to update
 * @param input - Updated task data
 * @returns Promise with update result
 */
export const updateTaskItem = async (
  itemId: string,
  input: TaskItemUpdateInput
): Promise<UpdateTaskItemResponse> => {
  const formattedInput: TaskItemUpdateInput = {
    ...input,
    ...(input.ItemTag === undefined &&
      input.Tags && {
        ItemTag: input.Tags.map(normalizeToItemTag),
      }),
  };

  const cleanInput: TaskItemUpdateInput = formattedInput;

  try {
    const response = await graphqlClient.request(UPDATE_TASK_MANAGER_ITEM_MUTATION, { ItemId: itemId, ...cleanInput, });

    return response;
  } catch (error) {
    console.error('Error updating task item:', error);
    throw error;
  }
};

/**
 * Deletes a task item
 * @param itemId - ID of the task to delete
 * @param isHardDelete - Whether to perform a hard delete
 * @returns Promise with deletion result
 */
export const deleteTaskItem = async (
  itemId: string,
  isHardDelete = false
): Promise<DeleteTaskItemResponse> => {
  const response = await graphqlClient.request(DELETE_TASK_MANAGER_ITEM_MUTATION, { ItemId: itemId, isHardDelete });

  return response;
};

/**
 * Creates a new task section
 * @param input - Section data
 * @returns Promise with creation result
 */
export const createTaskSection = async (
  input: TaskSectionInsertInput
): Promise<InsertTaskSectionResponse> => {
  try {
    const response = await graphqlClient.request(INSERT_TASK_MANAGER_SECTION_MUTATION, input);

    const responseData = response;

    if (!responseData?.insertTaskManagerSection) {
      throw new Error('Invalid response format from server');
    }

    return {
      insertTaskManagerSection: responseData.insertTaskManagerSection,
    };
  } catch (error) {
    console.error('Error in createTaskSection:', error);
    throw error;
  }
};

/**
 * Updates an existing task section
 * @param sectionId - ID of the section to update
 * @param input - Updated section data
 * @returns Promise with update result
 */
export const updateTaskSection = async (
  sectionId: string,
  input: TaskSectionUpdateInput
): Promise<UpdateTaskManagerSectionResponse> => {
  try {
    const response = await graphqlClient.request(UPDATE_TASK_MANAGER_SECTION_MUTATION, { ItemId: sectionId, ...input });

    if (!response) {
      throw new Error('No response received from server');
    }

    if (!response.updateTaskManagerSection) {
      throw new Error('No update data in response');
    }

    return {
      updateTaskManagerSection: response.updateTaskManagerSection,
    };
  } catch (error) {
    console.error('Error in updateTaskSection:', error);
    throw new Error(
      error instanceof Error
        ? error.message
        : 'An unknown error occurred while updating the section',
      { cause: error }
    );
  }
};

/**
 * Deletes a task section
 * @param sectionId - ID of the section to delete
 * @param isHardDelete - Whether to perform a hard delete
 * @returns Promise with deletion result
 */
export const deleteTaskSection = async (
  sectionId: string,
  isHardDelete = false
): Promise<DeleteTaskSectionResponse> => {
  const response = await graphqlClient.request(DELETE_TASK_MANAGER_SECTION_MUTATION, { ItemId: sectionId, isHardDelete });

  return response;
};

/**
 * Creates a new task tag
 * @param input - Task tag data
 * @returns Promise with creation result
 */
export const createTaskTag = async (input: TaskTagInsertInput): Promise<InsertTaskTagResponse> => {
  try {
    const response = await graphqlClient.request(INSERT_TASK_MANAGER_TAG_MUTATION, input);

    const responseData = response;

    if (!responseData) {
      throw new Error('No response data received from server');
    }

    if (!responseData.insertTaskManagerTag?.itemId) {
      throw new Error('No tag ID in response');
    }

    return {
      insertTaskManagerTag: {
        ...responseData.insertTaskManagerTag,
        itemId: responseData.insertTaskManagerTag.itemId,
      },
    };
  } catch (error) {
    console.error('Error in createTaskTag:', error);
    throw error;
  }
};

/**
 * Updates an existing task tag
 * @param itemId - ID of the task tag to update
 * @param input - Updated task data
 * @returns Promise with update result
 */
export const updateTaskTag = async (
  itemId: string,
  input: TaskTagUpdateInput
): Promise<UpdateTaskTagResponse> => {
  const response = await graphqlClient.request(UPDATE_TASK_MANAGER_TAG_MUTATION, { ItemId: itemId, ...input });

  return response;
};

/**
 * Deletes a task tag
 * @param itemId - ID of the task tag to delete
 * @param isHardDelete - Whether to perform a hard delete
 * @returns Promise with deletion result
 */
export const deleteTaskTag = async (
  itemId: string,
  isHardDelete = false
): Promise<DeleteTaskTagResponse> => {
  const response = await graphqlClient.request(DELETE_TASK_MANAGER_TAG_MUTATION, { ItemId: itemId, isHardDelete });

  return response;
};

/**
 * Creates a new comment for particular task
 * @param input - Task comment data
 * @returns Promise with creation result
 */
export const createTaskComment = async (
  input: TaskCommentInsertInput & { taskId?: string }
): Promise<InsertTaskCommentResponse> => {
  try {
    const now = new Date().toISOString();
    const { taskId: fallbackTaskId, ...commentInput } = input;
    const taskId = commentInput.TaskId ?? fallbackTaskId;

    if (!taskId) {
      throw new Error('Task ID is required to create a comment');
    }

    const mutationInput: TaskCommentInsertInput = {
      ...commentInput,
      TaskId: taskId,
      Timestamp: input.Timestamp ?? now,
      Author: input.Author ?? '',
    };

    const commentResponse = await graphqlClient.request(
      INSERT_TASK_COMMENTS_MUTATION,
      mutationInput
    );

    const responseData = commentResponse;

    if (!responseData) {
      throw new Error('No response data received from server');
    }

    if (!responseData.insertTaskComment?.itemId) {
      throw new Error('No comment ID in response');
    }

    return {
      insertTaskComment: {
        ...responseData.insertTaskComment,
        itemId: responseData.insertTaskComment.itemId,
      },
    };
  } catch (error) {
    console.error('Error in createTaskComment:', error);
    throw error;
  }
};

/**
 * Updates an existing particular task comment
 * @param itemId - ID of the task comment to update
 * @param input - Updated particular task comment data
 * @returns Promise with update result
 */
export const updateTaskComment = async (
  itemId: string,
  input: TaskCommentUpdateInput
): Promise<UpdateTaskCommentResponse> => {
  const now = new Date().toISOString();
  const mutationInput: TaskCommentUpdateInput = {
    ...input,
    Content: input.Content ?? '',
    Timestamp: input.Timestamp ?? now,
  };

  const cleanInput: TaskCommentUpdateInput = mutationInput;

  const response = await graphqlClient.request(UPDATE_TASK_COMMENTS_MUTATION, { ItemId: itemId, ...cleanInput, });

  return response;
};

/**
 * Deletes a particular task's comment
 * @param itemId - ID of the particular task's comment to delete
 * @param isHardDelete - Whether to perform a hard delete
 * @returns Promise with deletion result
 */
export const deleteTaskComment = async (
  itemId: string,
  isHardDelete = false
): Promise<DeleteTaskCommentResponse> => {
  try {
    const response = await graphqlClient.request(DELETE_TASK_COMMENTS_MUTATION, { ItemId: itemId, isHardDelete });

    const responseData = response;
    if (!responseData) {
      throw new Error('No response data received from server');
    }

    return responseData;
  } catch (error) {
    console.error('Error in deleteTaskComment:', error);
    throw error;
  }
};

export const getUsers = (payload: GetUsersPayload) => {
  const requestBody = {
    page: payload.page,
    pageSize: payload.pageSize,
    filter: {
      email: payload.filter?.email ?? '',
      name: payload.filter?.name ?? '',
    },
  };

  return clients.post<{
    data: IamData[];
    totalCount: number;
  }>('/idp/v1/Iam/GetUsers', JSON.stringify(requestBody));
};
